/* eslint-disable no-restricted-globals */
import { Injectable } from '@angular/core';
import { ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

import {
  AppProductLine as ProductLine,
  AppCustomLine as CustomLine
} from 'graphql/models/extended';
import { OfferBase } from 'models/offer.model';
import { StoreService } from 'shared/services/store.service';

import { PositionCustomPropertiesService } from './position-custom-properties.service';

@Injectable({
  providedIn: 'root'
})
export class PositionValidationService {
  constructor(
    private storeService: StoreService,
    private posCustomPropertiesService: PositionCustomPropertiesService
  ) { }

  private isEmptyInputValue(value: any): boolean {
    // we don't check for string here so it also works with arrays
    return value == null || value.length === 0;
  }

  validateMinShort(min: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.isEmptyInputValue(control.value) || this.isEmptyInputValue(min)) {
        return null; // don't validate empty values to allow optional controls
      }
      const value = parseFloat(control.value);

      // Controls with NaN values after parsing should be treated as not having a
      // minimum, per the HTML forms spec: https://www.w3.org/TR/html5/forms.html#attr-input-min
      return !isNaN(value) && value < min ? { minShort: { min, actual: control.value } } : null;
    };
  }

  validateMaxShort(max: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.isEmptyInputValue(control.value) || this.isEmptyInputValue(max)) {
        return null; // don't validate empty values to allow optional controls
      }
      const value = parseFloat(control.value);

      // Controls with NaN values after parsing should be treated as not having a
      // maximum, per the HTML forms spec: https://www.w3.org/TR/html5/forms.html#attr-input-max
      return !isNaN(value) && value > max ? { maxShort: { max, actual: control.value } } : null;
    };
  }

  validatePositionNumber(position: ProductLine | CustomLine): ValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors> => this.storeService.getCurrentOffer()
      .pipe(
        map((offer: OfferBase): ValidationErrors => {
          const positionNumber = this.posCustomPropertiesService.getPositionNumber(position);

          if (Number(positionNumber) !== Number(control.value)) {
            const positionNr = (offer.positionNumbers || []).some((i) => i === control.value);
            if (positionNr) {
              return { positionNr };
            }
          }

          return {};
        }),
        take(1)
      );
  }
}
